import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * FormLayout — sectioned form with sticky save bar. Pencil DuT72.
 *
 * Composition: multiple FormSection children + optional sticky footer
 * with save / discard. Also exposes an unsaved-changes prompt helper.
 */
export interface FormLayoutProps extends React.HTMLAttributes<HTMLFormElement> {
  dirty?: boolean
  submitting?: boolean
  onSave?: () => void | Promise<void>
  onDiscard?: () => void
  saveLabel?: string
  discardLabel?: string
  stickyFooter?: boolean
  footerSlot?: React.ReactNode
}

export const FormLayout = React.forwardRef<HTMLFormElement, FormLayoutProps>(
  (
    {
      className,
      dirty,
      submitting,
      onSave,
      onDiscard,
      saveLabel = 'Save changes',
      discardLabel = 'Discard',
      stickyFooter = true,
      footerSlot,
      children,
      onSubmit,
      ...props
    },
    ref,
  ) => (
    <form
      ref={ref}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit?.(e)
        onSave?.()
      }}
      className={cn('flex flex-col', className)}
      {...props}
    >
      <div className="flex flex-col gap-12 pb-12">{children}</div>
      {(onSave || footerSlot) && (
        <div
          className={cn(
            'flex items-center justify-between gap-3 border-t border-ia-border bg-card px-6 py-3',
            stickyFooter && 'sticky bottom-0',
          )}
        >
          <div className="text-caption text-ia-text-tertiary" aria-live="polite">
            {dirty ? 'You have unsaved changes' : 'All changes saved'}
          </div>
          <div className="flex items-center gap-2">
            {footerSlot}
            {onDiscard && (
              <Button
                variant="ghost"
                type="button"
                disabled={!dirty || submitting}
                onClick={onDiscard}
              >
                {discardLabel}
              </Button>
            )}
            {onSave && (
              <Button type="submit" loading={submitting} disabled={!dirty && !submitting}>
                {saveLabel}
              </Button>
            )}
          </div>
        </div>
      )}
    </form>
  ),
)
FormLayout.displayName = 'FormLayout'

/**
 * FormSection — labelled section inside a FormLayout. Pencil DuT72 inner rows.
 * Two-column layout: title/description on the left, form fields on the right.
 */
export interface FormSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title: React.ReactNode
  description?: React.ReactNode
  /** Put badges / status chips next to the section title */
  titleAside?: React.ReactNode
}

export const FormSection = React.forwardRef<HTMLElement, FormSectionProps>(
  ({ className, title, description, titleAside, children, ...props }, ref) => (
    <section
      ref={ref}
      className={cn(
        'grid grid-cols-12 gap-6 py-6 border-b border-ia-border last:border-b-0',
        className,
      )}
      {...props}
    >
      <div className="col-span-4 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-heading-sm text-ia-text">{title}</h2>
          {titleAside}
        </div>
        {description && (
          <p className="text-body-sm text-ia-text-secondary max-w-prose">{description}</p>
        )}
      </div>
      <div className="col-span-8 flex flex-col gap-4">{children}</div>
    </section>
  ),
)
FormSection.displayName = 'FormSection'
